import { useQuery } from "@apollo/client";
import { useMemo } from "react";

import DocumentationButton from "components/DocumentationButton";
import DocumentationIconButton from "components/DocumentationIconButton";
import {
  EmptystateCodeFileColored,
  EmptystateSearchNoResultsColored,
} from "components/icons/generated";
import PageLoading from "components/loading/PageLoading";
import DesktopView from "components/Mobile/DesktopView";
import MobileView from "components/Mobile/MobileView";
import SearchInput from "components/SearchInput";
import SortableTable, { SortableTableColumn } from "components/SortableTable";
import PrivateProviderRegistryTierInfo from "components/TierInfo/PrivateProviderRegistryTierInfo";
import ViewHeader from "components/ViewHeader";
import ViewHeaderTitle from "components/ViewHeader/Title";
import ViewHeaderWrapper from "components/ViewHeader/Wrapper";
import { URL_SEARCH_KEY } from "constants/url_query_keys";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeader from "ds/components/PageInfoHeader";
import Tooltip from "ds/components/Tooltip";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useTypedFlags from "hooks/useTypedFlags";
import useURLParams from "hooks/useURLParams";
import useCanCreateTerraformProvider from "shared/Terraform/access/useCanCreateTerraformProvider";
import { SearchQueryOrderDirection, TerraformProvider } from "types/generated";
import { fuzzySearch } from "utils/fuzzySearch";
import { getDocsUrl } from "utils/getDocsUrl";
import { AccountContext } from "views/AccountWrapper";

import { COLUMN_ORDER_PROVIDERS, FILTERS_ORDER_SETTINGS_KEY_PROVIDERS } from "./constants";
import { GET_PROVIDERS } from "./gql";
import { showProviderDrawer } from "./ProviderDrawer";
import ProviderListItem from "./ProviderListItem";
import { GetProvidersGql } from "./types";
import TerraformRegistryViewHeader from "./ViewHeader";

const columns: SortableTableColumn[] = [
  {
    value: "id",
    label: "Type",
  },
  {
    value: "space",
    label: "Space",
  },
  {
    value: "latestVersionNumber",
    label: "Latest version",
    static: true,
  },
];

function Providers() {
  const canCreateTerraformProvider = useCanCreateTerraformProvider();
  const { accountName } = useTypedContext(AccountContext);
  const { sharingModulesBetweenSpaces } = useTypedFlags();
  const showNewNavigation = sharingModulesBetweenSpaces;

  const handleOpenCreateDrawer = () => {
    showProviderDrawer();
  };

  const handleOpenUpdateDrawer = (provider: TerraformProvider) => {
    showProviderDrawer({
      id: provider.id,
      provider,
    });
  };

  const { error, loading, data } = useQuery<GetProvidersGql>(GET_PROVIDERS);
  const urlParams = useURLParams();
  const searchQuery = urlParams.get(URL_SEARCH_KEY);

  const providers = useMemo(() => {
    const filteredProviders = data?.terraformProviders || [];

    if (searchQuery) {
      return fuzzySearch(filteredProviders, searchQuery.trim(), {
        keys: ["description", "id", "labels"],
        scoreThreshold: -1000,
      });
    }

    return filteredProviders;
  }, [data, searchQuery]);

  useTitle(`Providers · ${accountName}`);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  const isLoading = loading;
  const isPageEmpty = !isLoading && !error && data?.terraformProviders.length === 0;
  const hasNoQueryResults = providers.length === 0 && searchQuery;

  return (
    <>
      {!showNewNavigation && <TerraformRegistryViewHeader />}
      {showNewNavigation && (
        <ViewHeader firstLevel>
          <ViewHeaderWrapper>
            <ViewHeaderTitle>Providers</ViewHeaderTitle>
            <MobileView>
              {(canCreateTerraformProvider.result || canCreateTerraformProvider.message) && (
                <Tooltip
                  active={!canCreateTerraformProvider.result}
                  on={(props) => (
                    <Button
                      {...props}
                      onPress={handleOpenCreateDrawer}
                      variant="primary"
                      disabled={isLoading || !canCreateTerraformProvider.result}
                    >
                      Create provider
                    </Button>
                  )}
                >
                  {canCreateTerraformProvider.message}
                </Tooltip>
              )}
            </MobileView>
          </ViewHeaderWrapper>
          <ViewHeaderWrapper>
            <SearchInput
              placeholder="Search by ID, description and labels..."
              filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY_PROVIDERS}
            />
            <DesktopView>
              <DocumentationIconButton
                href={getDocsUrl("/vendors/terraform/provider-registry")}
                tooltipText="Go to providers documentation"
              />
              {(canCreateTerraformProvider.result || canCreateTerraformProvider.message) && (
                <Tooltip
                  active={!canCreateTerraformProvider.result}
                  on={(props) => (
                    <Button
                      {...props}
                      onPress={handleOpenCreateDrawer}
                      variant="primary"
                      disabled={isLoading || !canCreateTerraformProvider.result}
                    >
                      Create provider
                    </Button>
                  )}
                >
                  {canCreateTerraformProvider.message}
                </Tooltip>
              )}
            </DesktopView>
          </ViewHeaderWrapper>
        </ViewHeader>
      )}
      {!showNewNavigation && (
        <PageInfoHeader title="Providers">
          <SearchInput
            placeholder="Search by ID, description and labels..."
            filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY_PROVIDERS}
          />

          <DesktopView>
            {(canCreateTerraformProvider.result || canCreateTerraformProvider.message) && (
              <Tooltip
                active={!canCreateTerraformProvider.result}
                on={(props) => (
                  <Button
                    {...props}
                    onPress={handleOpenCreateDrawer}
                    variant="primary"
                    disabled={isLoading || !canCreateTerraformProvider.result}
                  >
                    Create provider
                  </Button>
                )}
              >
                {canCreateTerraformProvider.message}
              </Tooltip>
            )}
          </DesktopView>
        </PageInfoHeader>
      )}
      <PrivateProviderRegistryTierInfo type="callout" />
      {isLoading && <PageLoading />}

      {!isLoading && isPageEmpty && !hasNoQueryResults && (
        <EmptyState
          title="Create your first Terraform provider"
          icon={EmptystateCodeFileColored}
          caption="You can use Spacelift to host your private Terraform providers."
        >
          <Box gap="medium">
            <DocumentationButton
              to={getDocsUrl("/vendors/terraform/provider-registry")}
              label="Documentation"
            />
            {(canCreateTerraformProvider.result || canCreateTerraformProvider.message) && (
              <Tooltip
                active={!canCreateTerraformProvider.result}
                on={(props) => (
                  <Button
                    {...props}
                    onPress={handleOpenCreateDrawer}
                    variant="primary"
                    disabled={!canCreateTerraformProvider.result}
                  >
                    Create provider
                  </Button>
                )}
              >
                {canCreateTerraformProvider.message}
              </Tooltip>
            )}
          </Box>
        </EmptyState>
      )}
      {!isLoading && hasNoQueryResults && (
        <EmptyState title="No results" icon={EmptystateSearchNoResultsColored} />
      )}
      {!isLoading && !isPageEmpty && (
        <SortableTable
          ariaLabel="Providers"
          items={providers}
          columns={columns}
          columnOrder={COLUMN_ORDER_PROVIDERS}
          renderItems={(sorted) =>
            sorted.map((item) => (
              <ProviderListItem key={item.id} item={item} onEdit={handleOpenUpdateDrawer} />
            ))
          }
          initialSortBy="revokedAt"
          initialDirection={SearchQueryOrderDirection.Desc}
        />
      )}
    </>
  );
}

export default Providers;
